import {
  Order,
  isAnonymousOrder,
  isAdvertiserWithOrganizationOrder,
  isPublisherOrder
} from '../types/order';
import { OrderModel } from '../model/objects/orderModel';
import { UserModel } from '../model/objects/userModel';
import { ResponseOrError, wrapError, wrapSuccess } from '../types/responses';
import { NewspaperOrderModel } from '../model/objects/newspaperOrderModel';
import { OrderDetailModel } from '../model/objects/orderDetailModel';
import { safeAsync } from '../safeWrappers';
import { getErrorReporter } from '../utils/errors';
import { ColumnService } from './directory';
import { isPublisher } from '../utils/users';

// Narrow helper to avoid casts when checking for anonymous access code
interface HasAccessCode {
  accessCode: string;
}
const hasAccessCode = (o: Partial<Order>): o is HasAccessCode =>
  Object.prototype.hasOwnProperty.call(o, 'accessCode');

export enum OrderOperation {
  PUBLISHER_EDIT = 'publisher.edit',
  EDIT = 'edit',
  CANCEL = 'cancel'
}

export type OrderOperationPrivilege = {
  allowed: boolean;
  reason?: string;
};

export type AuthorizedNewspaperOrder = NewspaperOrderModel & {
  canEdit: boolean;
};

export type OrderAuthorizationResult = {
  user: UserModel | null;
  privileges: Record<OrderOperation, OrderOperationPrivilege>;
  order: OrderModel;
  newspaperOrders: AuthorizedNewspaperOrder[];
  orderDetail: OrderDetailModel | null;
};

export class OrderAuthorizationService {
  public async authorize(
    order: OrderModel,
    {
      user,
      anonymousUser
    }: {
      user?: UserModel | null;
      anonymousUser?: { authEmail: string; accessCode: string };
    }
  ): Promise<ResponseOrError<OrderAuthorizationResult>> {
    const orderData = order.modelData;
    const logData = {
      orderId: order.id,
      userId: user?.id || 'anonymous',
      userEmail: user?.modelData.email || anonymousUser?.authEmail || 'none'
    };

    const [newspaperOrdersError, newspaperOrders] = await safeAsync(() =>
      order.getNewspaperOrders()
    )();
    if (newspaperOrdersError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        newspaperOrdersError,
        'Failed to get newspaper orders for authorization',
        logData
      );
      return wrapError(newspaperOrdersError);
    }

    const [orderDetailError, orderDetail] = await order.getOrderDetail();
    if (orderDetailError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        orderDetailError,
        'Failed to get order detail for authorization',
        logData
      );
      return wrapError(orderDetailError);
    }

    if (anonymousUser) {
      return this.handleAnonymous(
        order,
        orderData,
        anonymousUser,
        newspaperOrders,
        orderDetail
      );
    }
    if (user?.isPublisher) {
      return this.handlePublisher(
        order,
        orderData,
        user,
        newspaperOrders,
        orderDetail
      );
    }
    if (!user) {
      return wrapSuccess(
        this.buildDeniedAll(order, null, 'User not authenticated')
      );
    }
    return this.handleAdvertiser(
      order,
      orderData,
      user,
      newspaperOrders,
      orderDetail
    );
  }

  private handleAnonymous(
    order: OrderModel,
    orderData: Order,
    anonymousUser: { authEmail: string; accessCode: string },
    newspaperOrders: NewspaperOrderModel[],
    orderDetail: OrderDetailModel | null
  ): ResponseOrError<OrderAuthorizationResult> {
    const isAuthorizedAnonymous =
      isAnonymousOrder(orderData) &&
      hasAccessCode(orderData) &&
      orderData.contactEmail === anonymousUser.authEmail &&
      orderData.accessCode === anonymousUser.accessCode;
    if (isAuthorizedAnonymous) {
      return wrapSuccess(
        this.buildAllowedResult(order, null, newspaperOrders, orderDetail)
      );
    }
    return wrapSuccess(
      this.buildDeniedAll(order, null, 'Invalid anonymous credentials')
    );
  }

  private handlePublisher(
    order: OrderModel,
    orderData: Order,
    user: UserModel,
    newspaperOrders: NewspaperOrderModel[],
    orderDetail: OrderDetailModel | null
  ): ResponseOrError<OrderAuthorizationResult> {
    const userOrgIds = new Set(
      user.modelData.allowedOrganizations?.map(o => o.id)
    );
    if (isPublisherOrder(orderData)) {
      const publisherOrgId = orderData.authorizedOrganization?.id;
      if (publisherOrgId && userOrgIds.has(publisherOrgId)) {
        return wrapSuccess(
          this.buildAllowedResult(order, user, newspaperOrders, orderDetail)
        );
      }
      return wrapSuccess(
        this.buildDeniedAll(
          order,
          user,
          'User not in filing publisher organization'
        )
      );
    }

    const authorized = newspaperOrders.filter(no =>
      userOrgIds.has(no.modelData.newspaper.id)
    );
    if (authorized.length === 0) {
      return wrapSuccess(
        this.buildDeniedAll(order, user, 'No newspaper orders owned by user')
      );
    }
    return wrapSuccess(
      this.buildAllowedResult(order, user, authorized, orderDetail)
    );
  }

  private handleAdvertiser(
    order: OrderModel,
    orderData: Order,
    user: UserModel,
    newspaperOrders: NewspaperOrderModel[],
    orderDetail: OrderDetailModel | null
  ): ResponseOrError<OrderAuthorizationResult> {
    const isFiler = 'user' in orderData && user.ref.id === orderData.user.id;
    const belongsToOrg = this.userBelongsToAdvertiserOrg(orderData, user);
    const allow = isFiler || belongsToOrg;
    if (!allow) {
      return wrapSuccess(
        this.buildDeniedAll(
          order,
          user,
          'User not filer or in advertiser organization'
        )
      );
    }
    return wrapSuccess(
      this.buildAllowedResult(order, user, newspaperOrders, orderDetail)
    );
  }

  private userBelongsToAdvertiserOrg(
    orderData: Order,
    user: UserModel
  ): boolean {
    if (!isAdvertiserWithOrganizationOrder(orderData)) return false;
    const targetOrgId = orderData.advertiserOrganization.id;
    const allowed = user.modelData.allowedOrganizations || [];
    return allowed.some(org => org.id === targetOrgId);
  }

  private buildDeniedAll(
    order: OrderModel,
    user: UserModel | null,
    reason: string
  ): OrderAuthorizationResult {
    return {
      user,
      privileges: {
        [OrderOperation.PUBLISHER_EDIT]: { allowed: false, reason },
        [OrderOperation.EDIT]: { allowed: false, reason },
        [OrderOperation.CANCEL]: { allowed: false, reason }
      },
      order,
      newspaperOrders: [],
      orderDetail: null
    };
  }

  /**
   * TODO: Work with product to refine locking down an order to specific newspapers.  This is
   * a temporary allow-all to not create a regression.
   */
  private buildAllowedResult(
    order: OrderModel,
    user: UserModel | null,
    newspaperOrders: NewspaperOrderModel[],
    orderDetail: OrderDetailModel | null
  ): OrderAuthorizationResult {
    const userIsPublisher = user ? isPublisher(user) : false;
    return {
      user,
      privileges: {
        [OrderOperation.PUBLISHER_EDIT]: { allowed: userIsPublisher },
        [OrderOperation.EDIT]: { allowed: true },
        [OrderOperation.CANCEL]: { allowed: true }
      },
      order,
      newspaperOrders: newspaperOrders.map(no =>
        Object.assign(no, { canEdit: true })
      ),
      orderDetail: orderDetail || null
    };
  }
}
